import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from '@mui/material';
//import { alpha, useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachItem from './AttachItem';
import { notify } from 'utils/notify';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import {
  getSelectedItemsByLoggedInUserAndSubscription,
  removeItemFromSubscription,
} from './AppService';

const SelectedItemList = ({ appId, subscriptionId }) => {
  //const theme = useTheme();
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [appIdToDelete, setAppIdToDelete] = useState(null);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);

  useEffect(() => {
    if (appId) {
      console.log('Fetching items for appId:', appId); // Debug
      listAppItems(appId);
    } else {
      console.warn('appId is not available'); // Debug
    }
  }, [appId, subscriptionId]);

  const listAppItems = (appId) => {
    if (!appId) {
      console.error('Cannot fetch items. appId is undefined or null.'); // Debug
      return;
    }

    setLoading(true);
    getSelectedItemsByLoggedInUserAndSubscription(subscriptionId, appId)
      .then((response) => {
        console.log('Fetched items:', response.data); // Debug
        setItems(response.data);
      })
      .catch((error) => {
        console.error('Error fetching items for appId:', appId, error);
        notify(extractErrorMessage(error));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const selectItem = (itemId) => {
    console.log('Navigating to item details for itemId:', itemId);
    navigate(`/admin/dashboard/subscription/${subscriptionId}/item/${itemId}`);
  };

  const handleDeleteConfirmation = (subscriptionId, itemId) => {
    setAppIdToDelete(subscriptionId);
    setItemIdToDelete(itemId);
    setOpenDialog(true);
  };

  const handleDeleteItem = () => {
    removeItem(appIdToDelete, itemIdToDelete);
    setOpenDialog(false);
  };

  const removeItem = (subscriptionId, itemId) => {
    console.log('About to remove subscriptionId:', subscriptionId); // Debug
    removeItemFromSubscription(subscriptionId, itemId)
      .then(() => {
        console.log('Item removed successfully:', itemId); // Debug
        listAppItems(appId); // Refresh list after deletion
      })
      .catch((error) => {
        console.error('Error removing item:', itemId, error);
        if (error.response) {
          // Handle API errors
          if (
            error.response.status === 400 &&
            typeof error.response.data === 'string' &&
            error.response.data.includes('foreign key constraint')
          ) {
            notify(
              'Cannot delete the item because it is referenced by other records.',
            );
          } else if (error.response.status === 401) {
            notify(
              'Cannot delete or update a parent row: a foreign key constraint fails.',
            );
          } else {
            notify(
              'An error occurred while deleting the item. Please try again later.',
            );
          }
        } else {
          notify(
            'An error occurred while deleting the item. Please try again later.',
          );
        }
      });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" marginBottom={4}>
        <Typography variant="h4">Select Content</Typography>
      </Box>
      {loading && <Typography>Loading...</Typography>}
      <AttachItem listAppItems={() => listAppItems(appId)} appId={appId} />
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 750 }} aria-label="Selected Items Table">
          <TableHead sx={{ bgcolor: 'alternate.main' }}>
            <TableRow>
              <TableCell>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  ID
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Item Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Version Number
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Build Number
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Status
                </Typography>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.id}
                onClick={() => selectItem(item.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{item.id}</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.versionNumber}</TableCell>
                <TableCell>{item.buildNumber}</TableCell>
                <TableCell>{item.status}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation(); // Prevent row click
                      handleDeleteConfirmation(subscriptionId, item.id);
                    }}
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to remove this item?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteItem} autoFocus>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SelectedItemList;
